import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  Card,
  Image,
  Stack,
  CardBody,
  Heading,
  Text,
  Flex,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Menu,
  FormControl,
  FormLabel,
  Switch,
  LinkBox,
} from '@chakra-ui/react';
import PublishButton from 'components/common/buttons/PublishButton';
import EmailGroup from 'components/common/fields/EmailGroup';
import ProgressDisplay from 'components/course/ProgressDisplay';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { MdSettings } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { getClassProgressionsAction } from 'store/modules/class/class-action';
import { classActions } from 'store/modules/class/class-slice';
import { ReducerStatus } from 'utils/constants';

function ClassItem({ classObject, isAdminMode, isEditModeChecked, ...rest }) {
  const dispatch = useDispatch();
  const { id } = useParams();

  const classProgressions = useSelector((state) => state.class.progressions);
  const getClassProgressions = useSelector(
    (state) => state.class.getClassProgressions
  );

  const [progressionPercentage, setProgressionPercentage] = useState(0);

  const handleEditButton = () => {
    dispatch(classActions.setUpdateClass({ modal: true, id: classObject.id }));
  };

  const handleDeleteButton = () => {
    dispatch(classActions.setDeleteClass({ modal: true, id: classObject.id }));
  };

  useEffect(() => {
    dispatch(getClassProgressionsAction({ id: classObject.id, isAdminMode }));
  }, [classObject.id, isAdminMode, dispatch]);

  useEffect(() => {
    if (!classProgressions) return;
    const { totalSubQuestCount, progressions } = classProgressions;

    if (isAdminMode) {
      setProgressionPercentage(
        (progressions.filter((p) => p.dateFinished !== null).length /
          (totalSubQuestCount * classObject.users.length)) *
          100
      );
    } else {
      setProgressionPercentage(
        (progressions.filter((p) => p.dateFinished !== null).length /
          totalSubQuestCount) *
          100
      );
    }
  }, [classProgressions, classObject, isAdminMode]);

  const baseFileUrl = process.env.REACT_APP_FILE_URL;
  return (
    <Card {...rest} direction={{ base: 'column', sm: 'row' }} variant="outline">
      <Image
        objectFit="cover"
        maxW={{ base: '100%', sm: '300px' }}
        fallbackSrc={require('assets/default-class.jpg')}
        src={`${baseFileUrl}${classObject.thumbnailPath}`}
        alt={classObject.name}
      />

      <Stack flex={1}>
        <CardBody>
          <Stack gap={2} align="start">
            <Flex w="100%" align="center" gap={2}>
              <Heading size="md" flex={1}>
                {classObject.name}
              </Heading>

              {isAdminMode && (
                <Flex gap={4}>
                  {classObject?.isEditable && (
                    <FormControl
                      display="flex"
                      alignItems="center"
                      justifyContent="end"
                      w="fit-content"
                    >
                      <FormLabel htmlFor="edit-mode-on" fontSize="sm" mb="0">
                        Edit Mode
                      </FormLabel>
                      <LinkBox
                        as={Link}
                        to={
                          isEditModeChecked
                            ? `/classes/${id}`
                            : `/admin/classes/${id}`
                        }
                      >
                        <Switch
                          id="edit-mode-on"
                          isChecked={isEditModeChecked}
                        />
                      </LinkBox>
                    </FormControl>
                  )}

                  {isEditModeChecked && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        size={{ base: 'md', md: 'sm' }}
                        colorScheme="teal"
                        aria-label="Options"
                        icon={<MdSettings />}
                        variant="outline"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<EditIcon />}
                          onClick={handleEditButton}
                        >
                          Edit
                        </MenuItem>
                        <MenuItem
                          icon={<DeleteIcon />}
                          onClick={handleDeleteButton}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>
              )}
            </Flex>

            {isAdminMode && isEditModeChecked && (
              <PublishButton
                entityType="Class"
                entityId={classObject.id}
                isPublished={classObject.isPublished}
              />
            )}

            <Text>{classObject.description}</Text>

            <ProgressDisplay
              w="100%"
              isLoading={getClassProgressions.status === ReducerStatus.LOADING}
              percentage={progressionPercentage || 0}
              count={classObject.coursesCount || 0}
              countLabel={`Course${classObject.coursesCount > 1 ? 's' : ''}`}
            />

            <Text fontSize="xs" fontWeight={500}>
              Owner: {classObject.owner?.name || 'N/A'}
            </Text>

            {isAdminMode && (
              <EmailGroup
                name="Contributors:"
                emails={classObject.ownerGroups}
              />
            )}
          </Stack>
        </CardBody>
      </Stack>
    </Card>
  );
}

ClassItem.defaultProps = {
  isAdminMode: true,
  isEditModeChecked: false,
};

ClassItem.propTypes = {
  classObject: PropTypes.object.isRequired,
  isAdminMode: PropTypes.bool,
  isEditModeChecked: PropTypes.bool,
};

export default ClassItem;
